/**
 * messages.js
 * Message rendering and display logic
 */

import { elements } from './dom.js?v=302';
import { state } from './state.js?v=302';
import { icons } from './icons.js?v=302';
import { escapeHtml, formatTime, getRelativeTime } from './utils.js?v=302';
import { enhanceCodeBlocks } from './codeBlocks.js?v=302';
import { enhanceLinkPreviews } from './linkPreview.js?v=302';
import { enhanceImages, renderUserImagePreviews } from './media/imageRenderer.js?v=302';
import { enhanceVideoLinks } from './media/youtubeEmbed.js?v=302';
import { rewriteLocalMediaUrls } from './streamingMarkdown.js?v=302';
import { showToast, scrollToBottom } from './ui.js?v=302';
import { loadChat } from './chat.js?v=302';
import { generateResponse } from './api2.js?v=302';
import { saveChats } from './storage.js?v=302';


/**
 * Strip OpenClaw directive tags from displayed text.
 * Defense-in-depth: gateway should strip these, but we catch any leaks.
 * @param {string} text - Raw message text
 * @returns {string} Cleaned text
 */
function stripDirectiveTagsForDisplay(text) {
  if (!text) return text;
  let cleaned = text.replace(/\[\[\s*(?:reply_to_current|reply_to:\s*[^\]]*|audio_as_voice|tts(?::[^\]]*)?)\s*\]\]/g, '');
  cleaned = cleaned.replace(/\[\[tts:text\]\]([\s\S]*?)\[\[\/tts:text\]\]/g, '$1');
  cleaned = cleaned.replace(/<<<EXTERNAL_UNTRUSTED_CONTENT[^>]*>>>/g, '');
  cleaned = cleaned.replace(/<<<\/EXTERNAL_UNTRUSTED_CONTENT[^>]*>>>/g, '');
  // Strip gateway "No response from OpenClaw." fallback
  cleaned = cleaned.replace(/\s*No response from OpenClaw\.?\s*$/g, '');
  return cleaned.trim();
}

/**
 * Render a single message to the messages container
 * Handles session separators, message bubbles, and action buttons
 * @param {Object} message - Message object with id, role, content, timestamp
 * @param {Object} previousMessage - Previous message for session separator logic
 * @returns {HTMLElement} The rendered message wrapper element
 */
export function renderMessage(message, previousMessage = null) {
  // Session Separator Logic
  if (previousMessage) {
    const prevTime = new Date(previousMessage.timestamp);
    const currTime = new Date(message.timestamp);
    const diffMinutes = (currTime - prevTime) / (1000 * 60);

    if (diffMinutes > 30 || prevTime.getDate() !== currTime.getDate()) {
      const separator = document.createElement('div');
      separator.className = 'session-separator';

      const dateOptions = { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
      const dateStr = currTime.toLocaleDateString(undefined, dateOptions);

      separator.innerHTML = `<span class="session-date">${dateStr}</span>`;
      elements.messagesContainer.appendChild(separator);
    }
  }

  const wrapper = document.createElement("div");
  wrapper.className = `message-wrapper ${message.role}`;
  wrapper.dataset.id = message.id;

  // Minimal calm: no sender avatars in the message stream.

  // Create content wrapper
  const contentWrapper = document.createElement("div");
  contentWrapper.className = "message-content-wrapper";

  const bubble = document.createElement("div");
  bubble.className = "message-bubble";

  if (message.role === "user") {
    let contentHtml = `<div class="message-content">${escapeHtml(message.content)}</div>`;

    // Add file attachment indicator if files exist
    if (message.files && message.files.length > 0) {
      const fileIcons = {
        pdf: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>',
        docx: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>',
        xlsx: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="3" y1="15" x2="21" y2="15"></line><line x1="9" y1="3" x2="9" y2="21"></line><line x1="15" y1="3" x2="15" y2="21"></line></svg>',
      };

      // Separate image files from non-image files
      const isImageFile = (f) => {
        const t = (f.type || '').toLowerCase();
        return t === 'image' || t.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|bmp|ico|avif)$/i.test(f.name || '');
      };
      const imageFiles = message.files.filter(isImageFile);
      const nonImageFiles = message.files.filter(f => !isImageFile(f));

      // Render image thumbnails
      const imageThumbnailsHtml = renderUserImagePreviews(imageFiles);

      // Render non-image file pills
      const filesHtml = nonImageFiles.map(file => {
        const icon = fileIcons[file.type] || fileIcons.pdf;
        return `<div style="display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--accent-primary); margin-bottom: 4px;">
            <span>${icon}</span>
            <span>${escapeHtml(file.name)}</span>
          </div>`;
      }).join("");

      const hasAttachments = imageThumbnailsHtml || filesHtml;
      if (hasAttachments) {
        contentHtml = `<div style="margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid var(--border-subtle);">
          <div style="font-size: 11px; font-weight: 600; color: var(--text-tertiary); margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.5px;">Attached Files</div>
          ${imageThumbnailsHtml}${filesHtml}
        </div>` + contentHtml;
      }
    }

    bubble.innerHTML = contentHtml;
  } else {
    // For bot messages, handle empty content during streaming
    if (message.content === "" || !message.content) {
      bubble.innerHTML = `<div class="message-content"></div>`;
    } else {
      try {
        // Strip any leaked directive tags before rendering
        const cleanContent = stripDirectiveTagsForDisplay(message.content);
        let parsedContent = rewriteLocalMediaUrls(window.marked.parse(cleanContent));
        
         bubble.innerHTML = `<div class="message-content">${parsedContent}</div>`;
         setTimeout(() => {
           window.Prism.highlightAllUnder(bubble);
           enhanceCodeBlocks(bubble);
           enhanceLinkPreviews(bubble);
           enhanceImages(bubble);
           enhanceVideoLinks(bubble);
         }, 0);
       } catch (e) {
         bubble.innerHTML = `<div class="message-content">${escapeHtml(message.content)}</div>`;
       }
     }
  }

  const meta = document.createElement("div");
  meta.className = "message-meta";

  const timeStr = formatTime(message.timestamp);
  const fullDateStr = new Date(message.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const relativeTime = getRelativeTime(message.timestamp);

  // Build usage display for bot messages
  let usageHtml = '';
  if (message.role === "assistant" && message.usage) {
    const output = message.usage.output_tokens || message.usage.completion_tokens || 0;
    const total = message.usage.total_tokens || 0;
    // Per-response tokens for In/Out labels
    const input = total > output ? total - output : (message.usage.input_tokens || message.usage.prompt_tokens || 0);

    const fmtK = (n) => n >= 1000 ? (n / 1000).toFixed(1) + 'k' : n.toLocaleString();

    usageHtml = `
      <span class="meta-usage">
        | <span class="meta-usage-label">In</span> <span class="meta-usage-value">[${fmtK(input)}]</span>
        <span class="meta-usage-label">Out</span> <span class="meta-usage-value">[${fmtK(output)}]</span>
      </span>`;
  }

  let actionsHtml = `
            <span class="meta-time">${timeStr}</span>
            <span class="meta-full">• ${fullDateStr} • ${relativeTime}</span>${usageHtml}
            <div class="message-actions">
                <button class="action-btn copy-btn" title="Copy" aria-label="Copy message to clipboard">${icons.copy}</button>
        `;

  if (message.role === "assistant") {
    actionsHtml += `<button class="action-btn regenerate-btn" title="Regenerate Response" aria-label="Regenerate response">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path><path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path><path d="M16 16h5v5"></path></svg>
        </button>`;
  }

  if (message.role === "user") {
    actionsHtml += `
                <button class="action-btn edit-btn" title="Edit Message" aria-label="Edit message">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                </button>
                <button class="action-btn delete-btn" title="Delete Message" aria-label="Delete message">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                </button>`;
  }

  actionsHtml += `</div>`;
  meta.innerHTML = actionsHtml;

  // Add reasoning section for bot messages if reasoning exists - BEFORE content
  if (message.role === "assistant" && message.reasoning) {
    const reasoningSection = document.createElement("div");
    reasoningSection.className = "reasoning-section";
    reasoningSection.innerHTML = `
      <details open>
        <summary>🧠 Internal Reasoning</summary>
        <div class="reasoning-section-content">${escapeHtml(message.reasoning)}</div>
      </details>
    `;
    contentWrapper.appendChild(reasoningSection);
  }

  contentWrapper.appendChild(bubble);
  contentWrapper.appendChild(meta);

  wrapper.appendChild(contentWrapper);
  elements.messagesContainer.appendChild(wrapper);

  // Copy button functionality
  const copyBtn = meta.querySelector(".copy-btn");
  copyBtn.addEventListener("click", () => {
    navigator.clipboard.writeText(message.content);
    copyBtn.innerHTML = icons.check;
    setTimeout(function () {
      copyBtn.innerHTML = icons.copy;
    }, 2000);
  });

  // Regenerate Handler
  const regenBtn = meta.querySelector(".regenerate-btn");
  if (regenBtn) {
    regenBtn.addEventListener("click", () => {
      if (state.isGenerating) return;

      const currentChat = state.chats.find((c) => c.id === state.currentChatId);
      if (!currentChat) return;

      const msgIndex = currentChat.messages.findIndex((m) => m.id === message.id);
      if (msgIndex === -1) return;

      // Truncate history: remove this message and everything after
      currentChat.messages = currentChat.messages.slice(0, msgIndex);

      // Save and reload
      saveChats();
      loadChat(state.currentChatId);

      // Regenerate
      generateResponse(currentChat);
    });
  }

  // Edit button functionality
  const editBtn = meta.querySelector(".edit-btn");
  if (editBtn) {
    editBtn.addEventListener("click", () => {
      if (state.isGenerating) {
        showToast("Wait for the current response to finish", "info");
        return;
      }

      // 1. Get current content
      const contentDiv = wrapper.querySelector(".message-content");
      const currentText = message.content;

      // 2. Create textarea
      const textarea = document.createElement("textarea");
      textarea.className = "message-edit-textarea";
      textarea.value = currentText;

      // 3. Create action buttons
      const editActions = document.createElement("div");
      editActions.className = "message-edit-actions";
      editActions.innerHTML = `
  <button class="btn btn-primary save-edit-btn">Send</button>
  <button class="btn btn-secondary cancel-edit-btn">Cancel</button>
`;

      // 4. Replace content with edit UI
      contentDiv.innerHTML = "";
      contentDiv.appendChild(textarea);
      contentDiv.appendChild(editActions);
      textarea.focus();

      // 5. Save handler
      editActions.querySelector(".save-edit-btn").addEventListener("click", () => {
        const newContent = textarea.value.trim();
        if (!newContent) {
          showToast("Message cannot be empty", "error");
          return;
        }

        // Update message in state
        const currentChat = state.chats.find(c => c.id === state.currentChatId);
        const msgIndex = currentChat.messages.findIndex(m => m.id === message.id);
        if (msgIndex === -1) return;

        currentChat.messages[msgIndex].content = newContent;

        // Truncate to keep only up to and including this message
        currentChat.messages = currentChat.messages.slice(0, msgIndex + 1);

        // Save and reload
        saveChats();
        loadChat(state.currentChatId);

        // Regenerate response from this point
        generateResponse(currentChat);

        showToast("Message updated", "success");
      });

      // 6. Cancel handler
      editActions.querySelector(".cancel-edit-btn").addEventListener("click", () => {
        loadChat(state.currentChatId); // Just reload to restore
      });
    });
  }

  // Delete button functionality
  const deleteBtn = meta.querySelector(".delete-btn");
  if (deleteBtn) {
    deleteBtn.addEventListener("click", () => {
      if (state.isGenerating) {
        showToast("Wait for the current response to finish", "info");
        return;
      }

      // Create confirmation modal
      const modal = document.createElement("div");
      modal.className = "modal-overlay visible";
      modal.innerHTML = `
  <div class="modal-card" style="max-width: 500px;">
    <div class="modal-header">
      <h3 class="modal-title">Delete Message?</h3>
      <p class="modal-subtitle">This will remove your message and all responses after it. This action cannot be undone.</p>
    </div>
    <div class="modal-actions">
      <button class="btn btn-secondary cancel-delete-btn">Cancel</button>
      <button class="btn btn-primary confirm-delete-btn" style="background: linear-gradient(135deg, var(--error), #a04845);">Delete</button>
    </div>
  </div>
`;
      document.body.appendChild(modal);

      // Cancel handler
      modal.querySelector(".cancel-delete-btn").addEventListener("click", () => {
        modal.remove();
      });

      // Close on overlay click
      modal.addEventListener("click", (e) => {
        if (e.target === modal) modal.remove();
      });

      // Confirm handler
      modal.querySelector(".confirm-delete-btn").addEventListener("click", () => {
        const currentChat = state.chats.find(c => c.id === state.currentChatId);
        if (!currentChat) return;

        const msgIndex = currentChat.messages.findIndex(m => m.id === message.id);
        if (msgIndex === -1) return;

        // Remove message and everything after it
        currentChat.messages = currentChat.messages.slice(0, msgIndex);

        // Save and reload
        saveChats();
        loadChat(state.currentChatId);

        modal.remove();
        showToast("Message deleted", "info");
      });
    });
  }

  // Check if user is scrolled up
  const container = elements.messagesContainer;
  const isScrolledUp = container.scrollTop + container.clientHeight < container.scrollHeight - 100;

  if (isScrolledUp && message.role === "assistant") {
    state.newMessageCount++;
    const btn = document.getElementById("scroll-bottom-btn");
    if (btn) {
      btn.classList.add("visible");
      btn.classList.add("has-new");
      const countSpan = btn.querySelector(".new-count");
      if (countSpan) countSpan.textContent = `${state.newMessageCount} new`;
    }
  } else if (!state.isReading) {
    scrollToBottom();
  }

  return wrapper;
}
